import React, {useState} from 'react';
import {BaseCRUDPage} from '../common';
import {Input, RequiredIndicator, Column, FieldError} from '../../components';
import {makeService} from '../../services';
import {Make, MakeCreate, MakeUpdate} from '../../types';
import {formatDate} from '../../utils/tableUtils';

interface MakeFormData {
    code: string;
    name: string;
}

const MakesPage: React.FC = () => {
    const [formData, setFormData] = useState<MakeFormData>({
        code: '',
        name: ''
    });

    // Define table columns
    const columns: Column[] = [
        {key: 'code', label: 'Code', sortable: true, searchable: true},
        {key: 'name', label: 'Name', sortable: true, searchable: true},
        {
            key: 'created_at',
            label: 'Created At',
            sortable: true,
            render: (value: string) => formatDate(value)
        }
    ];

    // Form fields component with error support
    const formFields = (fieldErrors: Record<string, string>) => (
        <div className="space-y-3">
            <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Make Code <RequiredIndicator/>
                </label>
                <Input
                    type="text"
                    value={formData.code}
                    onChange={(e) => setFormData(prev => ({...prev, code: e.target.value}))}
                    required
                    placeholder="e.g., TOYOTA"
                />
                <FieldError error={fieldErrors.code}/>
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Make Name <RequiredIndicator/>
                </label>
                <Input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({...prev, name: e.target.value}))}
                    required
                    placeholder="e.g., Toyota Motor Corporation"
                />
                <FieldError error={fieldErrors.name}/>
            </div>
        </div>
    );

    // Modal icon
    const modalIcon = (
        <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
            <svg className="w-6 h-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor"
                 viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z"/>
            </svg>
        </div>
    );

    return (
        <BaseCRUDPage<Make, MakeCreate, MakeUpdate>
            title="Make Management"
            entityName="make"
            columns={columns}
            service={makeService}
            formFields={formFields}
            formData={formData}
            setFormData={setFormData}
            requiredFields={['code', 'name']}
            getInitialFormData={() => ({code: '', name: ''})}
            toCreateData={(formData) => ({
                code: formData.code,
                name: formData.name
            })}
            toUpdateData={(formData, original) => {
                const updateData: MakeUpdate = {};
                if (formData.code !== original.code) updateData.code = formData.code;
                if (formData.name !== original.name) updateData.name = formData.name;
                return updateData;
            }}
            toFormData={(make) => ({
                code: make.code,
                name: make.name
            })}
            modalIcon={modalIcon}
            modalTitle={(isEdit) => ({
                title: isEdit ? 'Edit Make' : 'Create New Make',
                subtitle: isEdit ? 'Update make information' : 'Add a new make to the system'
            })}
            customDeleteConfirm={(make) => `Are you sure you want to delete make "${make.name}"?`}
            defaultSearchColumn="code"
        />
    );
};

export default MakesPage;
